import React, { useRef } from 'react';
import { FormControlLabel, Stack, Switch, Typography } from '@curves/ui-base';
import { Overview, OverflowMenu } from '@curves/ui-shared';

import { displayDateText } from '@Util/moment.util';
import { usePromotionPlanContext } from '@Local/PromotionPlan/Components/PromotionPlanContext';
import usePromotionPlanApi from '@Apis/usePromotionPlanApi';
/**
 * 生成行銷活動資訊
 * @params {string} planInfo - 行銷活動相關的資訊物件
 * @params {Object} planInfo.freeType - 會籍前或會籍後的文字描述
 * @params {number} planInfo.joinFee - 入會費金額
 * @params {string} planInfo.freeTypeText - 會籍前或會籍後的文字描述
 * @params {number} planInfo.freeMonth - 贈送的會籍月份數
 * @params {number} planInfo.freeDay - 贈送的會籍天數
 * @returns {string} - 行銷活動生成的文字
 */
const generatePlanText = ({ freeTypeText, freeMonth, freeDay }) => {
    // 生成贈送文字
    let freeText = '';
    if (freeMonth > 0 && freeDay > 0) {
        freeText = `贈送${freeTypeText}${freeMonth}個月又${freeDay}天`;
    } else if (freeMonth > 0) {
        freeText = `贈送${freeTypeText}${freeMonth}個月`;
    } else if (freeDay > 0) {
        freeText = `贈送${freeTypeText}${freeDay}天`;
    }
    return freeText;
};
const PlanEnableSwitch = (props) => {
    const { defaultValue, planID, refresh } = props;
    const { putPromotionPlanEnableApi } = usePromotionPlanApi();
    const apiSendingRef = useRef(false);
    const handlePromotionPlanEnableApi = async (planID, params) => {
        const resp = await putPromotionPlanEnableApi(planID, params);
        if (resp) {
            apiSendingRef.current = false;
            if (typeof refresh === 'function') refresh();
        }
    };
    const handleChangeSwitch = (e, checked) => {
        let params = { enabled: checked };
        if (!apiSendingRef.current) {
            apiSendingRef.current = true;
            handlePromotionPlanEnableApi(planID, params);
        }
    };
    return <FormControlLabel className={'m-0'} control={<Switch key={defaultValue} defaultChecked={defaultValue} onChange={handleChangeSwitch} />} />;
};
const headerRow = [
    {
        cellKey: 'planName',
        headerLabel: '項目名稱',
        style: { minWidth: 50 },
        formatCell: (planName, { planCode }) => {
            return (
                <Stack alignItems={'start'} direction={'column'}>
                    <Typography component={'span'}>{planName}</Typography>
                    <Typography component={'span'} variant={'body2'}>
                        {planCode}
                    </Typography>
                </Stack>
            );
        },
    },
    {
        cellKey: 'planDescription',
        headerLabel: '項目說明',
        style: { minWidth: 50 },
    },
    {
        cellKey: 'joinFee',
        headerLabel: '設定內容',
        style: { minWidth: 50 },
        formatCell: (joinFee, rowData) => {
            const planText = generatePlanText(rowData);
            return (
                <>
                    <Typography className={'mb-0'}>{`入會費$${joinFee}元`}</Typography>
                    {planText && <Typography className={'mb-0'}>{planText}</Typography>}
                </>
            );
        },
    },
    {
        cellKey: 'startDate',
        headerLabel: '生效時間',
        style: { minWidth: 50 },
        formatCell: (startDate, { endDate }) => {
            return displayDateText(startDate, endDate);
        },
    },
    {
        cellKey: 'isEnabled',
        headerLabel: '是否生效',
        align: 'center',
        style: { minWidth: 50 },
        formatCell: (isEnabled, { planID }, { refresh }) => {
            return <PlanEnableSwitch defaultValue={isEnabled} planID={planID} refresh={refresh} />;
        },
    },
    {
        cellKey: 'more',
        headerLabel: '',
        width: 6.8,
        style: { minWidth: 50 },
        formatCell: (value, rowData, { openPromotionPlanDialog, refresh }) => {
            const menuData = [
                {
                    text: '編輯方案',
                    onClick: (e) => {
                        openPromotionPlanDialog(rowData, refresh);
                    },
                },
            ];
            return <OverflowMenu menuData={menuData} actionMenuProps={{ className: 'more-label-box-menu align-left' }} />;
        },
    },
];
const PromotionPlanTable = () => {
    const { dialogRef, sourceData, bookmark, setBookmarkNum, refresh } = usePromotionPlanContext();
    const { pageIndex = 1, pageSize = bookmark.pageSize, totalCount = 0, totalPage = 0, list = [] } = sourceData || {};
    const handleChangePage = (e, num) => {
        if (typeof setBookmarkNum === 'function') setBookmarkNum(num);
    };
    const updateOverviewPage = (num) => {
        if (document.activeElement) document.activeElement.blur();
        handleChangePage(null, num);
    };
    const contextValue = {
        overviewData: { list, pagination: { pageIndex, pageSize, totalCount, totalPage } },
        updateOverviewPage,
    };
    return (
        <Overview contextValue={contextValue} idKey="planID">
            <Overview.Table
                headerRow={headerRow}
                cardProps={{ className: 'theme-gray' }}
                rowClassName={(row) => (row.isEnabled === false ? 'overview-item-invalid' : '')}
                cellRenderContext={{
                    refresh,
                    openPromotionPlanDialog: dialogRef.current && dialogRef.current.openPromotionPlanDialog,
                }}
            />
            <Overview.Pagination className="mt-4" />
        </Overview>
    );
};
export default PromotionPlanTable;
